import { ReactNode } from "react";
import { ITimelineAnimTrackSvgData, ITimelineItemDetails } from "./hooks";

export const enum TimelineTypeEnum {
  Horizontal = "horizontal",
  Vertical = "vertical",
}

export const enum TimelineStyleEnum {
  Line = "line",
  Path = "path",
}

export const enum TimelinePositionEnum {
  Left = "left",
  Center = "center",
  Right = "right",
  Top = "top",
  Middle = "middle",
  Bottom = "bottom",
}

export const enum TimelineSetContainerEnum {
  Full = "full",
  SideBySide = "side-by-side",
}

export const enum TimelineMarkerEnum {
  Bullets = "bullets",
  Numbers = "numbers",
  Arrows = "arrows",
  CustomText = "custom-text",
}

export const enum TimelineContentWrappingEnum {
  Carousel = "carousel",
  NewLine = "new-line",
}

export interface ITimelineSpecificSettings {
  timelineType: TimelineTypeEnum;
  timelineStyle: TimelineStyleEnum;
  timelinePosition: TimelinePositionEnum;
  timelineSetContainer: TimelineSetContainerEnum;
  timelineMarker: TimelineMarkerEnum;
  timelineSetsPerSlide: "auto" | number;
  timelineColor: string;
  /** Label color inside the wide chevron marker (Wide Items / `h-wide` preset). */
  timelineMarkerTextColor?: string;
  timelineContentWrapping?: TimelineContentWrappingEnum;
  timelinePresetId?: string;
  timelineContentPlacement?: "default" | "alternating" | "split";
  timelineArrowVariant?: "small" | "big";
  // Optional shape wrapper applied around the marker content. When set, the
  // marker label (number / custom-text) is rendered INSIDE the shape.
  // Design-locked by the preset, not exposed in the Layout popover.
  timelineMarkerShape?: "arrow-big";
  // Hide the connecting line between items (line mode). Used by designs
  // where markers alone form the visual flow (e.g., Arrows preset).
  timelineHideLine?: boolean;
  // Snake ordering: odd rows left-to-right, even rows right-to-left so the
  // connecting line visually snakes across rows. Used by the Bottom Line
  // preset which renders as a wrapped, continuous boustrophedon track.
  timelineSnakeOrder?: boolean;
}

export interface ITimelineSettings extends ITimelineSpecificSettings {
  // Would be nice to have a general type with all base settings for combos since they're mostly the same everywhere
  // or maybe just with types for each setting

  name: string;
  verticalAlignment?: string;
  horizontalAlignment?: string;

  backgroundColor?: string;

  borderColor?: string;
  borderWidth?: number;

  round?: string;

  minHeight?: string;
  isCustomHeight?: boolean;
  maxWidth?: string;
  isCustomWidth?: boolean;

  // Would be nice to have a general type somewhere with animation types, so we could just extend this type with it
  isAnimationActive?: boolean;
  animationType?: string;
  animationSpeed?: string;
  animationBaseOpacity?: string;
  animationDirection?: string;
}

// Ideally we would extend actual set settings here
export interface ITimelineSetSettings {
  [key: string]: unknown;

  timelineMarkerText?: string;
}

export interface ITimelineEditorData {
  isThumbnailsGridView: boolean;
}

export interface ITimelineTrackSegmentProps {
  itemIdx: number;
  setSettings: ITimelineSetSettings;
  /** Editor-only: full TimelineMarker-wrapped editable text node, used by
   *  presets where the marker is a standalone text element. */
  editableMarkerSlot?: ReactNode;
  /** Editor-only: bare editable text node (no marker wrapper), used by
   *  presets where the marker shape itself is the wrapper (e.g. h-wide's
   *  wide chevron with a centred label). */
  editableTextSlot?: ReactNode;
  trackSvgData: ITimelineAnimTrackSvgData;

  itemDetails: ITimelineItemDetails;
}
